import type { components } from "@majitap/sdk";
import { Button, formatMoney } from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Link } from "@tanstack/react-router";
import { useCompanyFormat } from "../../company";
import { INVOICE_STATUS } from "../../invoices";
import { PAYMENT_STATUS } from "../../labels";
import { SideCard } from "./order-side";
import styles from "./record.module.css";

type AdminOrder = components["schemas"]["AdminOrder"];
export type CollectKind = "record" | "stk" | "postpaid" | "unpaid";

/**
 * The payment (`Console / Order / Redesign`): its status and term, paid against due, the invoices
 * it is on (P8-47), and the ways to collect (P8-39) for staff who may record payments. Once it is
 * on an invoice, it is collected on the invoice instead. A refunded order can take a new payment,
 * or be marked unpaid to collect again.
 */
export function PaymentCard({
  order: o,
  termName,
  onCredit,
  owed,
  canCollect,
  onCollect,
}: {
  order: AdminOrder;
  termName: string | null;
  /** on a credit term: billed on account, so it isn't let go "before payment" */
  onCredit: boolean;
  /** money is still owed on it */
  owed: boolean;
  canCollect: boolean;
  onCollect: (kind: CollectKind) => void;
}) {
  const format = useCompanyFormat();
  const [label, tone] = PAYMENT_STATUS[o.payment_status];
  return (
    <SideCard title="Payment">
      <div className={styles.rowTop}>
        <Badge tone={tone}>{label}</Badge>
        {onCredit ? <Badge tone="info">Credit</Badge> : null}
        <span className={styles.small}>{termName ?? "No payment term"}</span>
      </div>
      {onCredit && owed && !o.invoices.length ? (
        <span className={styles.small}>
          A credit customer: billed on account and invoiced at the end of the period. Deliver it as usual; there's
          nothing to collect first.
        </span>
      ) : null}
      <div className={styles.payLabels}>
        <span className={styles.small}>Paid {formatMoney(o.paid_amount, format)}</span>
        <strong>Due {formatMoney(o.due_amount, format)}</strong>
      </div>
      {o.invoices.length ? (
        <ul className={styles.invoiceList} aria-label="Invoices">
          {o.invoices.map((i) => (
            <li key={i.id} className={styles.rowTop}>
              <Link to="/invoices/$invoiceId" params={{ invoiceId: i.id }} className={styles.link}>
                {i.invoice_number ?? "Draft invoice"}
              </Link>
              <Badge tone={INVOICE_STATUS[i.status][1]}>{INVOICE_STATUS[i.status][0]}</Badge>
            </li>
          ))}
        </ul>
      ) : null}
      {owed && o.invoices[0] ? (
        <span className={styles.small}>
          On {o.invoices[0].invoice_number ?? "a draft invoice"}: collect the payment on the invoice.
        </span>
      ) : null}
      {canCollect ? (
        <div className={styles.buttons}>
          <Button variant="secondary" onClick={() => onCollect("stk")}>
            Send M-Pesa request
          </Button>
          <Button variant="secondary" onClick={() => onCollect("record")}>
            Record payment
          </Button>
          {o.payment_status === "refunded" ? (
            <Button variant="secondary" onClick={() => onCollect("unpaid")}>
              Mark unpaid
            </Button>
          ) : o.postpaid || onCredit ? null : (
            <Button variant="secondary" onClick={() => onCollect("postpaid")}>
              Deliver before payment
            </Button>
          )}
        </div>
      ) : null}
    </SideCard>
  );
}
